import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineSearch } from 'react-icons/ai'
import { FiCalendar, FiX } from 'react-icons/fi'
import { MwSelector } from '../ui'
import CollapsibleFilters, { filterChipRemoveStyle, filterChipStyle } from '../ui/CollapsibleFilters'
import TicketStatusChips from './TicketStatusChips'
import TicketDateFilter from './TicketDateFilter'
import { DEFAULT_TICKET_PERIOD, TICKET_PERIODS, formatTicketPeriodRange, normalizeTicketPeriod } from '../../utils/ticketDateRange'

// شريط فلاتر التذاكر (الفلاتر + الفترة الزمنية + الحالات بعدّاداتها + البحث).
// مشترك بين /tickets و /reports — أي تعديل في الشكل هنا بيظهر في الصفحتين.
// الفلاتر التفصيلية بتتلم في لوحة بتتفتح بأنيميشن، والحالات والبحث بيفضلوا ظاهرين.
const TicketFiltersBar = ({
    lang = 'ar',
    filters = {},
    options = {},
    showAssignee = false,
    onFilterChange,
    period,
    from = '',
    to = '',
    onPeriodChange,
    statuses,
    activeStatus = 'all',
    getStatusCount,
    onStatusChange,
    searchValue = '',
    onSearchChange,
}) => {
    const [t, i18n] = useTranslation('global');
    const isRtl = i18n.dir() === 'rtl';
    const isAr = lang === 'ar';

    const selectedOf = (key) => {
        const list = options[key] || [];
        return list.find((item) => String(item.value) === String(filters[key] ?? 'all')) || list[0] || null;
    };

    const selectFields = [
        { key: 'product', label: t('support.tickets.product'), initial: t('support.tickets.allProducts'), style: { minWidth: 200, flex: '1 1 200px' } },
        { key: 'section', label: t('support.tickets.section'), initial: t('support.tickets.allSections'), style: { minWidth: 200, flex: '1 1 200px' } },
        { key: 'priority', label: t('support.tickets.priority'), initial: isAr ? 'كل الأولويات' : 'All priorities', style: { minWidth: 180, flex: '1 1 180px' } },
        { key: 'tenantId', label: isAr ? 'فلترة حسب العميل' : 'Filter by Customer', initial: isAr ? 'كل العملاء' : 'All Customers', style: { minWidth: 200, flex: '1 1 200px' } },
        { key: 'visibility', label: isAr ? 'فلترة حسب نوع التذكرة' : 'Filter by Ticket Type', initial: isAr ? 'كل الأنواع' : 'All Types', style: { minWidth: 180, flex: '1 1 180px' } },
        ...(showAssignee
            ? [{ key: 'assignee', label: isAr ? 'فلترة حسب المعيّن' : 'Filter by Assignee', initial: isAr ? 'الكل' : 'All', style: { minWidth: 220, flex: '1 1 220px' }, avatar: true }]
            : []),
    ];

    const chips = selectFields
        .filter((field) => filters[field.key] && String(filters[field.key]) !== 'all')
        .map((field) => ({
            key: field.key,
            label: field.label,
            value: selectedOf(field.key)?.label || String(filters[field.key]),
            onRemove: () => onFilterChange?.(field.key, 'all'),
        }));
    const activePeriod = normalizeTicketPeriod(period);
    const periodMeta = TICKET_PERIODS.find((item) => item.value === activePeriod) || TICKET_PERIODS[0];
    const periodChanged = activePeriod !== DEFAULT_TICKET_PERIOD;
    const periodRange = formatTicketPeriodRange(activePeriod, from, to, lang);
    const resetPeriod = () => onPeriodChange?.({ period: DEFAULT_TICKET_PERIOD, from: '', to: '' });

    const clearAll = () => {
        chips.forEach((item) => item.onRemove());
        if (periodChanged) resetPeriod();
    };

    // الفترة الزمنية دايماً ظاهرة، والضغط عليها بيفتح اللوحة.
    const periodChip = (openPanel) => (
        <button
            type="button"
            onClick={openPanel}
            title={periodRange || undefined}
            style={{
                ...filterChipStyle,
                paddingInlineEnd: periodChanged ? 6 : 10,
                background: periodChanged ? '#eef2ff' : '#f8fafc',
                borderColor: periodChanged ? '#e0e7ff' : '#e2e8f0',
                color: periodChanged ? '#3730a3' : '#475569',
                cursor: 'pointer',
                animation: 'none',
            }}
        >
            <FiCalendar size={12} />
            <span>{isAr ? periodMeta.ar : periodMeta.en}</span>
            {periodRange && <span style={{ fontWeight: 600, color: '#94a3b8', fontSize: 10.5 }}>{periodRange}</span>}
            {periodChanged && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={isAr ? 'رجوع للفترة الافتراضية' : 'Reset period'}
                    onClick={(e) => { e.stopPropagation(); resetPeriod(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); resetPeriod(); } }}
                    style={filterChipRemoveStyle}
                >
                    <FiX size={11} />
                </span>
            )}
        </button>
    );

    return (
        <CollapsibleFilters
            lang={lang}
            storageKey="support.ticketFilters.open"
            chips={chips}
            activeCount={chips.length + (periodChanged ? 1 : 0)}
            leading={periodChip}
            onClearAll={clearAll}
            footer={(
                <TicketStatusChips
                    lang={lang}
                    statuses={statuses}
                    activeStatus={activeStatus}
                    getStatusCount={getStatusCount}
                    onStatusChange={onStatusChange}
                    singleRow
                >
                    <div style={{ width: '100%' }}>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <AiOutlineSearch
                                size={16}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    [isRtl ? 'right' : 'left']: 11,
                                    color: '#94a3b8'
                                }}
                            />
                            <input
                                type="text"
                                value={searchValue}
                                onChange={(e) => onSearchChange?.(e.target.value)}
                                placeholder={isAr ? 'ابحث برقم التذكرة أو عنوانها...' : 'Search by ticket number or subject...'}
                                style={{
                                    width: '100%',
                                    height: 34,
                                    padding: isRtl ? '0 34px 0 10px' : '0 10px 0 34px',
                                    borderRadius: 8,
                                    border: '1px solid #e2e8f0',
                                    fontSize: 12,
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    background: '#f8fafc'
                                }}
                                onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                            />
                        </div>
                    </div>
                </TicketStatusChips>
            )}
        >
            {selectFields.map((field) => (
                <div key={field.key} style={field.style}>
                    <MwSelector
                        label={field.label}
                        initalValue={field.initial}
                        dataType='labelValue'
                        _data={options[field.key] || []}
                        selectedItem={selectedOf(field.key)}
                        setSelectedItem={(item) => onFilterChange?.(field.key, item?.value || 'all')}
                        withAddNew={false}
                        showAvatarFallback={Boolean(field.avatar)}
                    />
                </div>
            ))}
            <div style={{ minWidth: 200, flex: '1 1 200px' }}>
                <TicketDateFilter
                    lang={lang}
                    period={period}
                    from={from}
                    to={to}
                    onChange={onPeriodChange}
                />
            </div>
        </CollapsibleFilters>
    );
};

export default TicketFiltersBar
